import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canCreateAsset, canViewFinancial, formatINR, formatDate, hasCapability } from "@/lib/permissions";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Search, Package, MapPin, Upload, Download, FileSpreadsheet,
  CheckCircle2, XCircle, Filter, X, Printer, Settings2, Trash2, QrCode, Pencil,
} from "lucide-react";
import { SmartSearchBar } from "@/components/AIFeatures";

const STATUSES = ["all", "active", "under_maintenance", "retired", "disposed"];
const TYPES = ["all", "tangible", "intangible"];

const STATUS_STYLES = {
  active: "bg-green-50 text-green-700 border-green-200",
  under_maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  retired: "bg-slate-100 text-slate-600 border-slate-200",
  disposed: "bg-red-50 text-red-700 border-red-200",
};

export default function Assets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [assetType, setAssetType] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [location, setLocation] = useState("all");
  const [manufacturer, setManufacturer] = useState("all");
  const [brand, setBrand] = useState("all");
  const [filterValues, setFilterValues] = useState({ locations: [], manufacturers: [], brands: [] });
  const [categories, setCategories] = useState({ tangible: [], intangible: [] });
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [selPrintOpen, setSelPrintOpen] = useState(false);
  const [smartResults, setSmartResults] = useState(null); // when set, overrides `assets` for display
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const showFinancial = canViewFinancial(user?.role);
  const canDeleteAsset = hasCapability(user, "delete_asset");

  const removeAsset = async (asset) => {
    const label = `${asset.asset_id || ""} — ${asset.name || ""}`.trim();
    if (!window.confirm(`Delete "${label}" permanently?\n\nThis removes the asset and all its maintenance history. This action cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success(`Deleted ${asset.asset_id}`);
      setAssets((prev) => prev.filter((x) => x.id !== asset.id));
      setSmartResults((prev) => (prev ? prev.filter((x) => x.id !== asset.id) : prev));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const availableCategories = useMemo(() => {
    if (assetType === "tangible") return categories.tangible;
    if (assetType === "intangible") return categories.intangible;
    return [...categories.tangible, ...categories.intangible];
  }, [assetType, categories]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (assetType !== "all") params.asset_type = assetType;
    if (category !== "all") params.category = category;
    if (status !== "all") params.status = status;
    if (location !== "all") params.location = location;
    if (manufacturer !== "all") params.manufacturer = manufacturer;
    if (brand !== "all") params.brand = brand;
    const { data } = await api.get("/assets", { params });
    setAssets(data);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const [cats, vals] = await Promise.all([
        api.get("/meta/categories"),
        api.get("/assets/filters/values"),
      ]);
      setCategories({ tangible: cats.data.tangible, intangible: cats.data.intangible });
      setFilterValues(vals.data);
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetType, category, status, location, manufacturer, brand]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    if (category !== "all" && assetType !== "all") {
      const allowed = assetType === "tangible" ? categories.tangible : categories.intangible;
      if (!allowed.includes(category)) setCategory("all");
    }
    // eslint-disable-next-line
  }, [assetType, categories]);

  const clearFilters = () => {
    setQ(""); setAssetType("all"); setCategory("all"); setStatus("all");
    setLocation("all"); setManufacturer("all");
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === assets.length && assets.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(assets.map((a) => a.id)));
    }
  };

  const printLabels = async (idsArg, opts = {}) => {
    const ids = idsArg && idsArg.length ? idsArg : [...selected];
    if (ids.length === 0) return;
    const startRow = Math.max(1, Math.min(Number(opts.startRow) || 1, 6));
    const startCol = Math.max(1, Math.min(Number(opts.startCol) || 1, 3));
    setPrinting(true);
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/assets/labels/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ asset_ids: ids, start_row: startRow, start_col: startCol }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate PDF");
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `asset-labels-${ids.length}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${ids.length} label(s)`);
      if (!idsArg) setSelected(new Set());
    } catch (e) {
      toast.error(e.message || "Print failed");
    } finally { setPrinting(false); }
  };

  const activeFilterCount = [assetType, category, status, location, manufacturer, brand].filter(v => v !== "all").length + (q ? 1 : 0);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Registry</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            Asset Registry
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{assets.length} assets · Real-time</p>
        </div>
        {canCreateAsset(user?.role) && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setRangeOpen(true)}
              data-testid="print-qr-range-btn"
              className="border-[#7C1D3A] text-[#7C1D3A] hover:bg-[#fcf0f3]"
            >
              <QrCode className="w-4 h-4 mr-2" /> Print QR (Range)
            </Button>
            {selected.size > 0 && (
              <Button
                onClick={() => setSelPrintOpen(true)}
                disabled={printing}
                variant="outline"
                data-testid="print-labels-btn"
                className="border-[#7C1D3A] text-[#7C1D3A] hover:bg-[#fcf0f3]"
              >
                <Printer className="w-4 h-4 mr-2" />
                {printing ? "Generating..." : `Print Labels (${selected.size})`}
              </Button>
            )}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-import-btn">
                  <Upload className="w-4 h-4 mr-2" /> Bulk Import
                </Button>
              </DialogTrigger>
              <BulkImportDialog onDone={() => { setBulkOpen(false); load(); }} />
            </Dialog>
            <Button
              onClick={() => setManageCatsOpen(true)}
              variant="outline"
              data-testid="manage-asset-categories-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" /> Categories
            </Button>
            <Button
              onClick={() => navigate("/assets/new")}
              data-testid="new-asset-btn"
              className="bg-[#7C1D3A] hover:bg-[#6c1c36] text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Asset
            </Button>
          </div>
        )}
      </div>
      {manageCatsOpen && <ManageAssetCategoriesDialog open={manageCatsOpen} onOpenChange={setManageCatsOpen} />}
      {rangeOpen && (
        <PrintQRRangeDialog
          open={rangeOpen}
          onOpenChange={setRangeOpen}
          allAssets={smartResults ?? assets}
          onPrint={(ids, pos) => { setRangeOpen(false); printLabels(ids, pos); }}
          busy={printing}
        />
      )}
      {selPrintOpen && (
        <SelectedPrintDialog
          open={selPrintOpen}
          onOpenChange={setSelPrintOpen}
          count={selected.size}
          onConfirm={(pos) => {
            setSelPrintOpen(false);
            printLabels(null, pos);
          }}
          busy={printing}
        />
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <SmartSearchBar
            onResults={(results) => setSmartResults(results)}
            onClear={() => setSmartResults(null)}
          />
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, serial, or asset ID..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="asset-search"
                className="pl-9 h-10"
              />
            </div>
            <Select value={assetType} onValueChange={setAssetType}>
              <SelectTrigger className="w-[150px]" data-testid="filter-asset-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t === "all" ? "All Types" : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {availableCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger className="w-[170px]" data-testid="filter-brand">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {(filterValues.brands || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s === "all" ? "All Statuses" : s.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllFilters((v) => !v)}
              data-testid="toggle-filters"
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" /> {showAllFilters ? "Less" : "More"} filters
            </Button>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters" className="text-slate-500">
                <X className="w-3.5 h-3.5 mr-1" /> Clear ({activeFilterCount})
              </Button>
            )}
          </div>

          {showAllFilters && (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="w-[220px]" data-testid="filter-location">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {filterValues.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={manufacturer} onValueChange={setManufacturer}>
                <SelectTrigger className="w-[200px]" data-testid="filter-manufacturer">
                  <SelectValue placeholder="Invoicing Party" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invoicing Parties</SelectItem>
                  {filterValues.manufacturers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                {canCreateAsset(user?.role) && (
                  <th className="pl-4 pr-2 py-3 w-8">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-[#7C1D3A] focus:ring-[#7C1D3A] cursor-pointer"
                      checked={selected.size > 0 && selected.size === assets.length}
                      onChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th className="px-4 py-3">Asset ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Deployed To</th>
                <th className="px-4 py-3">Purchase Date</th>
                {showFinancial && <th className="px-4 py-3 text-right">Purchase Value</th>}
                {showFinancial && <th className="px-4 py-3 text-right">Current Value</th>}
                <th className="px-4 py-3">Status</th>
                {canDeleteAsset && <th className="px-4 py-3 text-right w-16">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="assets-table">
              {loading ? (
                <tr><td colSpan={(canCreateAsset(user?.role) ? 12 : 11) + (canDeleteAsset ? 1 : 0)} className="px-4 py-12 text-center text-slate-400">Loading assets...</td></tr>
              ) : (smartResults ?? assets).length === 0 ? (
                <tr><td colSpan={(canCreateAsset(user?.role) ? 12 : 11) + (canDeleteAsset ? 1 : 0)} className="px-4 py-16 text-center">
                  <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <div className="text-slate-500 text-sm">No assets match your filters.</div>
                </td></tr>
              ) : (smartResults ?? assets).map((a) => (
                <tr
                  key={a.id}
                  className={`table-row-hover cursor-pointer ${selected.has(a.id) ? "bg-[#fcf0f3]/50" : ""}`}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  data-testid={`asset-row-${a.asset_id}`}
                >
                  {canCreateAsset(user?.role) && (
                    <td className="pl-4 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-[#7C1D3A] focus:ring-[#7C1D3A] cursor-pointer"
                        checked={selected.has(a.id)}
                        onChange={() => toggleSelect(a.id)}
                        data-testid={`select-${a.asset_id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{a.asset_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.manufacturer} {a.model_number}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    {a.brand ? <span className="font-semibold">{a.brand}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={a.asset_type === "intangible" ? "border-blue-200 text-blue-700 bg-blue-50 capitalize" : "border-slate-200 text-slate-600 capitalize"}>
                      {a.asset_type || "—"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{a.category}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="flex items-center gap-1 text-xs">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      {a.location || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs" data-testid={`deployment-${a.asset_id}`}>
                    {a.deployment
                      ? (a.deployment.type === "line" ? <span><span className="font-medium">{a.deployment.line_name}</span></span>
                        : a.deployment.type === "department" ? <span className="text-blue-700">Dept · {a.deployment.department_name}</span>
                        : a.deployment.type === "person" ? <span>@{a.deployment.person_name}{a.deployment.user_id ? "" : <span className="text-slate-400"> (ext)</span>}</span>
                        : "—")
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{formatDate(a.purchase_date)}</td>
                  {showFinancial && <td className="px-4 py-3 text-right font-mono text-slate-900">{formatINR(a.purchase_value)}</td>}
                  {showFinancial && <td className="px-4 py-3 text-right font-mono text-[#7C1D3A] font-semibold">{formatINR(a.current_value)}</td>}
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_STYLES[a.status]} border text-xs font-medium capitalize`}>
                      {a.status?.replace("_", " ")}
                    </Badge>
                  </td>
                  {canDeleteAsset && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => removeAsset(a)}
                        className="text-slate-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors"
                        title="Delete asset"
                        data-testid={`delete-asset-${a.asset_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function BulkImportDialog({ onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assets/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created} of ${data.total_rows} assets`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setUploading(false); }
  };

  const downloadTemplate = async (format) => {
    const token = localStorage.getItem("assetiq_token");
    try {
      const res = await fetch(`${API_BASE}/assets/bulk/template?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `asset_import_template.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Failed to download template"); }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-[#7C1D3A]" /> Bulk Asset Import
        </DialogTitle>
      </DialogHeader>

      {!result ? (
        <form onSubmit={submit} className="space-y-4">
          <div className="p-4 bg-[#fcf0f3] border border-[#f5c4d3] rounded-lg text-sm">
            <div className="font-semibold text-[#7C1D3A] mb-1">1. Download the template</div>
            <p className="text-xs text-slate-600 mb-2">
              Required columns: <b>name</b>, <b>category</b>, <b>purchase_date</b> (YYYY-MM-DD), <b>purchase_value</b>. Asset IDs are auto-generated. For intangibles, only <b>Software, Antivirus, Firewall, License, Other Intangibles</b> are allowed.
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("csv")} data-testid="download-csv-template">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV Template
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("xlsx")} data-testid="download-xlsx-template">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Excel Template
              </Button>
            </div>
          </div>

          <div className="p-4 border border-dashed border-slate-300 rounded-lg text-sm">
            <div className="font-semibold text-slate-700 mb-2">2. Upload your filled file</div>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="bulk-file-input"
              className="cursor-pointer"
            />
            {file && (
              <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
                <FileSpreadsheet className="w-3.5 h-3.5" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={!file || uploading} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="bulk-upload-submit">
              {uploading ? "Uploading..." : "Import Assets"}
            </Button>
          </DialogFooter>
        </form>
      ) : (
        <div className="space-y-3" data-testid="bulk-result">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Rows</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{result.total_rows}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">Created</div>
              <div className="font-heading text-2xl font-bold text-green-700 mt-1">{result.created}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs uppercase tracking-wider text-red-700 font-semibold">Failed</div>
              <div className="font-heading text-2xl font-bold text-red-700 mt-1">{result.failed}</div>
            </div>
          </div>

          {result.created_assets.length > 0 && (
            <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2 bg-slate-50 border-b border-slate-200">Created</div>
              <div className="divide-y divide-slate-100">
                {result.created_assets.map((a) => (
                  <div key={a.asset_id} className="px-3 py-2 text-xs flex items-center justify-between">
                    <span className="text-slate-900">{a.name}</span>
                    <span className="font-mono text-slate-500 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-600" /> {a.asset_id}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="border border-red-200 rounded-lg max-h-40 overflow-y-auto">
              <div className="text-xs font-semibold text-red-700 uppercase tracking-wider px-3 py-2 bg-red-50 border-b border-red-200">Errors</div>
              <div className="divide-y divide-red-100">
                {result.errors.map((err, i) => (
                  <div key={`row-${err.row}-${i}`} className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-1 text-red-700 font-medium">
                      <XCircle className="w-3 h-3" /> Row {err.row}: {err.name || "(no name)"}
                    </div>
                    <div className="text-slate-600 mt-0.5">{err.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setResult(null); setFile(null); }}>Import More</Button>
            <Button onClick={onDone} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="bulk-done-btn">Done</Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  );
}

function ManageAssetCategoriesDialog({ open, onOpenChange }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState("tangible");
  const [prefix, setPrefix] = useState("");
  const [busy, setBusy] = useState(false);
  // Feb 2026 — inline edit: id of the row currently in edit mode + its draft.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", prefix: "", default_maintenance_months: 12, useful_life_years: 10, salvage_percent: 5 });
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/asset-categories");
      setCats(data);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/asset-categories", {
        name: name.trim(), asset_type: assetType,
        prefix: prefix.trim() || null, default_maintenance_months: 12,
      });
      toast.success("Category added");
      setName(""); setPrefix("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setBusy(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditDraft({
      name: c.name || "",
      prefix: c.prefix || "",
      default_maintenance_months: c.default_maintenance_months || 12,
      useful_life_years: c.useful_life_years || (c.asset_type === "intangible" ? 5 : 10),
      salvage_percent: c.salvage_percent ?? 5,
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    if (!editDraft.name.trim()) { toast.error("Name cannot be blank"); return; }
    if (!editDraft.prefix.trim()) { toast.error("Prefix cannot be blank"); return; }
    const life = Number(editDraft.useful_life_years);
    if (!life || life < 1 || life > 100) { toast.error("Useful life must be between 1 and 100 years"); return; }
    const salv = Number(editDraft.salvage_percent);
    if (isNaN(salv) || salv < 0 || salv > 100) { toast.error("Salvage % must be between 0 and 100"); return; }
    setEditBusy(true);
    try {
      await api.patch(`/asset-categories/${editingId}`, {
        name: editDraft.name.trim(),
        prefix: editDraft.prefix.trim().toUpperCase(),
        default_maintenance_months: Number(editDraft.default_maintenance_months) || 12,
        useful_life_years: life,
        salvage_percent: salv,
      });
      toast.success("Category updated");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    } finally { setEditBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this category? Existing assets with this category are not affected.")) return;
    try {
      await api.delete(`/asset-categories/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const tang = cats.filter((c) => c.asset_type === "tangible");
  const intang = cats.filter((c) => c.asset_type === "intangible");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-heading">Manage Asset Categories</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
          {[
            { label: "Tangible Categories", list: tang },
            { label: "Intangible Categories", list: intang },
          ].map(({ label, list }) => (
            <div key={label}>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-1">{label}</div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                {loading ? <div className="p-3 text-slate-400 text-xs">Loading...</div>
                  : list.length === 0 ? <div className="p-3 text-slate-400 text-xs">None yet</div>
                    : list.map((c) => (
                      editingId === c.id ? (
                        <div key={c.id} className="p-3 bg-[#fcf0f3]/60" data-testid={`asset-cat-edit-row-${c.name}`}>
                          <div className="grid grid-cols-6 gap-2">
                            <div className="col-span-3">
                              <Label className="text-[10px] text-slate-500 uppercase">Name</Label>
                              <Input value={editDraft.name} onChange={(e) => setEditDraft(d => ({ ...d, name: e.target.value }))} className="h-9" data-testid="edit-cat-name" />
                            </div>
                            <div className="col-span-1">
                              <Label className="text-[10px] text-slate-500 uppercase">Prefix</Label>
                              <Input value={editDraft.prefix} maxLength={5}
                                onChange={(e) => setEditDraft(d => ({ ...d, prefix: e.target.value.toUpperCase() }))}
                                className="h-9 font-mono" data-testid="edit-cat-prefix" />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[10px] text-slate-500 uppercase">Maint (months)</Label>
                              <Input type="number" min={1} max={120} value={editDraft.default_maintenance_months}
                                onChange={(e) => setEditDraft(d => ({ ...d, default_maintenance_months: e.target.value }))}
                                className="h-9" data-testid="edit-cat-months" />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[10px] text-slate-500 uppercase">Useful Life (years)</Label>
                              <Input type="number" min={1} max={100} value={editDraft.useful_life_years}
                                onChange={(e) => setEditDraft(d => ({ ...d, useful_life_years: e.target.value }))}
                                className="h-9" data-testid="edit-cat-useful-life" />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[10px] text-slate-500 uppercase">Salvage (%)</Label>
                              <Input type="number" min={0} max={100} step="0.01" value={editDraft.salvage_percent}
                                onChange={(e) => setEditDraft(d => ({ ...d, salvage_percent: e.target.value }))}
                                className="h-9" data-testid="edit-cat-salvage" />
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-2">
                            Renaming updates every existing asset with this category. Prefix change applies to <b>future</b> asset IDs only.
                            Useful life &amp; salvage are used by SLM depreciation for every asset in this category (asset-level overrides still win).
                          </p>
                          <div className="flex justify-end gap-2 mt-2">
                            <Button size="sm" variant="outline" disabled={editBusy} onClick={cancelEdit} data-testid="edit-cat-cancel">Cancel</Button>
                            <Button size="sm" disabled={editBusy} onClick={saveEdit} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="edit-cat-save">
                              {editBusy ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div key={c.id} className="flex items-center justify-between px-3 py-2" data-testid={`asset-cat-row-${c.name}`}>
                          <div>
                            <div className="font-medium text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                              <span>Prefix: <span className="font-mono">{c.prefix}</span></span>
                              <span>Maint every {c.default_maintenance_months || 12} months</span>
                              <span>Useful life: <b>{c.useful_life_years ?? (c.asset_type === "intangible" ? 5 : 10)}</b> yrs</span>
                              <span>Salvage: <b>{(c.salvage_percent ?? 5)}%</b></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => startEdit(c)} className="text-slate-500 hover:text-[#7C1D3A] p-1.5" title="Edit" data-testid={`asset-cat-edit-${c.name}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 p-1.5" title="Remove">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )
                    ))}
              </div>
            </div>
          ))}

          <div className="border-t border-slate-200 pt-3">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">Add New Category</div>
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-3">
                <Input placeholder="Category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-asset-cat-name" />
              </div>
              <div className="col-span-2">
                <select value={assetType} onChange={(e) => setAssetType(e.target.value)} className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm h-10" data-testid="new-asset-cat-type">
                  <option value="tangible">Tangible</option>
                  <option value="intangible">Intangible</option>
                </select>
              </div>
              <div className="col-span-1">
                <Input placeholder="Prefix" value={prefix} maxLength={5} onChange={(e) => setPrefix(e.target.value.toUpperCase())} data-testid="new-asset-cat-prefix" title="Optional 2-5 letter code used in Asset IDs (auto-generated if blank)" />
              </div>
              <Button onClick={add} disabled={busy || !name.trim()} className="col-span-6 bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-asset-cat-btn">
                {busy ? "Adding..." : "Add Category"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Prefix is used to generate Asset IDs (e.g. <span className="font-mono">MAC-XXXX-00001</span>). Leave blank to auto-derive from the name.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ---------------------------------------------------------------------------
// Print QR — Range picker
// User enters a numeric "From" and "To" sequence; we scan the trailing digits
// of every asset_id and pick the matching subset. Optional prefix (dropdown of
// unique code prefixes) further narrows it down. Uses the SAME PDF endpoint,
// so label size / layout stays identical.
// ---------------------------------------------------------------------------
function PrintQRRangeDialog({ open, onOpenChange, allAssets, onPrint, busy }) {
  const [fromSeq, setFromSeq] = useState("");
  const [toSeq, setToSeq] = useState("");
  const [prefix, setPrefix] = useState("all");
  const [startRow, setStartRow] = useState(1);
  const [startCol, setStartCol] = useState(1);

  // Extract trailing digits from every asset_id — e.g. "MAC-JUKI-00039" -> 39
  const parsed = useMemo(() => (allAssets || []).map((a) => {
    const code = a.asset_id || "";
    const m = code.match(/(\d+)\s*$/);
    const seq = m ? parseInt(m[1], 10) : null;
    // Prefix is everything before the trailing digit group
    const pref = m ? code.slice(0, m.index).replace(/-+$/, "") : code;
    return { ...a, __seq: seq, __prefix: pref };
  }), [allAssets]);

  const prefixes = useMemo(() => {
    const s = new Set(parsed.map((a) => a.__prefix).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [parsed]);

  const matching = useMemo(() => {
    const from = fromSeq === "" ? -Infinity : parseInt(fromSeq, 10);
    const to = toSeq === "" ? Infinity : parseInt(toSeq, 10);
    if (isNaN(from) || isNaN(to) || from > to) return [];
    return parsed.filter((a) =>
      a.__seq !== null &&
      a.__seq >= from &&
      a.__seq <= to &&
      (prefix === "all" || a.__prefix === prefix)
    );
  }, [parsed, fromSeq, toSeq, prefix]);

  const canPrint = matching.length > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="print-qr-range-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#7C1D3A]" /> Print QR Labels — Sequence Range
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Enter the numeric sequence range (from the tail of the Asset ID, e.g. <span className="font-mono">MAC-JUKI-<b>00039</b></span> ⇒ 39). Optional prefix filter narrows the range within one category.
          </p>

          {prefixes.length > 2 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prefix</label>
              <Select value={prefix} onValueChange={setPrefix}>
                <SelectTrigger className="mt-1" data-testid="range-prefix-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All prefixes</SelectItem>
                  {prefixes.filter((p) => p !== "all").map((p) => (
                    <SelectItem key={p} value={p} className="font-mono">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">From sequence #</label>
              <Input
                type="number" min="1"
                value={fromSeq}
                onChange={(e) => setFromSeq(e.target.value)}
                placeholder="e.g. 1"
                data-testid="range-from-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">To sequence #</label>
              <Input
                type="number" min="1"
                value={toSeq}
                onChange={(e) => setToSeq(e.target.value)}
                placeholder="e.g. 50"
                data-testid="range-to-input"
              />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="range-preview">
            {fromSeq === "" || toSeq === "" ? (
              <span className="text-slate-500">Enter a range to see how many labels will be printed.</span>
            ) : matching.length === 0 ? (
              <span className="text-red-700">No assets fall in that range with the selected prefix.</span>
            ) : (
              <>
                <div className="font-semibold text-slate-800">
                  {matching.length} label{matching.length !== 1 ? "s" : ""} will be printed
                </div>
                <div className="text-xs text-slate-500 font-mono mt-1 line-clamp-2">
                  {matching.slice(0, 6).map((a) => a.asset_id).join(" · ")}
                  {matching.length > 6 && ` · +${matching.length - 6} more`}
                </div>
              </>
            )}
          </div>

          <SheetPositionPicker
            startRow={startRow}
            startCol={startCol}
            onChange={(r, c) => { setStartRow(r); setStartCol(c); }}
            count={matching.length}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="range-cancel-btn">Cancel</Button>
          <Button
            onClick={() => onPrint(matching.map((a) => a.id), { startRow, startCol })}
            disabled={!canPrint}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="range-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {busy ? "Generating…" : `Print ${matching.length || ""} Label${matching.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Shared visual picker for the starting row / column on a partially-consumed
 * A4 sticker sheet. Renders a 3×6 grid where the user taps the first
 * *available* sticker slot (rows already peeled off appear as skipped).
 * Feb 2026 — added to prevent sticker sheet wastage.
 */
function SheetPositionPicker({ startRow, startCol, onChange, count }) {
  const ROWS = 6, COLS = 3;
  const skipped = (startRow - 1) * COLS + (startCol - 1);
  const perPage = ROWS * COLS;
  const onFirstPage = Math.min(count || 0, perPage - skipped);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Start Position on Sheet</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Tap the first <b>available</b> sticker slot. Slots already used will be skipped.
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] font-mono text-slate-700">Row {startRow} · Col {startCol}</div>
          <button
            type="button"
            onClick={() => onChange(1, 1)}
            className="text-[11px] text-[#7C1D3A] hover:underline mt-0.5"
            data-testid="sheet-position-reset"
          >
            Reset to top-left
          </button>
        </div>
      </div>
      <div className="inline-grid gap-1 p-2 bg-slate-50 rounded" style={{ gridTemplateColumns: `repeat(${COLS}, 34px)` }} data-testid="sheet-position-grid">
        {Array.from({ length: ROWS * COLS }).map((_, i) => {
          const row = Math.floor(i / COLS) + 1;
          const col = (i % COLS) + 1;
          const isSkipped = i < skipped;
          const isFirst = row === startRow && col === startCol;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(row, col)}
              data-testid={`sheet-slot-r${row}-c${col}`}
              className={`h-6 rounded text-[10px] font-mono border transition-colors ${
                isFirst
                  ? "bg-[#7C1D3A] text-white border-[#7C1D3A]"
                  : isSkipped
                    ? "bg-slate-200 text-slate-400 border-slate-300 line-through"
                    : "bg-white text-slate-500 border-slate-300 hover:border-[#7C1D3A] hover:text-[#7C1D3A]"
              }`}
              title={isSkipped ? "Already used" : `Start here (R${row} C${col})`}
            >
              {row},{col}
            </button>
          );
        })}
      </div>
      {count > 0 && (
        <div className="text-[11px] text-slate-500 mt-2">
          {skipped > 0
            ? <>Skipping the first <b>{skipped}</b> slot{skipped !== 1 ? "s" : ""} on page 1. </>
            : "Starting from top-left (full sheet). "}
          {count <= perPage - skipped
            ? <>{count} sticker{count !== 1 ? "s" : ""} on this sheet.</>
            : <>{onFirstPage} on this sheet · {count - onFirstPage} on additional page{(count - onFirstPage) > perPage ? "s" : ""} (from top-left).</>}
        </div>
      )}
    </div>
  );
}


/**
 * Compact dialog to pick sheet start position when printing from
 * the "Print Labels (N)" (selection-based) button.
 */
function SelectedPrintDialog({ open, onOpenChange, count, onConfirm, busy }) {
  const [startRow, setStartRow] = useState(1);
  const [startCol, setStartCol] = useState(1);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="selected-print-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#7C1D3A]" /> Print {count} QR Label{count !== 1 ? "s" : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <SheetPositionPicker
            startRow={startRow}
            startCol={startCol}
            onChange={(r, c) => { setStartRow(r); setStartCol(c); }}
            count={count}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sel-print-cancel-btn">Cancel</Button>
          <Button
            onClick={() => onConfirm({ startRow, startCol })}
            disabled={busy}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="sel-print-confirm-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {busy ? "Generating…" : "Print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
